import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import MainLayout from '../../layout/main-layout';
import ComptableLayout from '../../layout/comptable-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import formatNumber, {
  defaultFilterDates,
  formatDate,
  DUO_PERIOD_OPTIONS,
  getPeriodDateRange,
} from '../../utils/functions';
import DuoModal from '../../components/ui/duo-modal';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';

const PER_PAGE = 10;
const today = new Date().toISOString().split('T')[0];

const PERIOD_ALL = { label: 'Tout', value: '' };

const RAPPORT_EXTRA_PERIODS = [
  { label: 'Cette semaine', value: 'this_week' },
  { label: 'Ce trimestre', value: 'this_quarter' },
  { label: 'Cette année', value: 'this_year' },
];

const periodOptionsWithAll = [PERIOD_ALL, ...DUO_PERIOD_OPTIONS, ...RAPPORT_EXTRA_PERIODS];

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const localYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const getStartOfQuarter = (date) => {
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1);
};

const buildReportApiParams = (filters) => {
  const params = {};
  if (filters.userId) params.user_id = filters.userId;

  if (filters.period) {
    const range = getPeriodDateRange(filters.period);
    if (range) {
      if (range.from) params.start_date = localYmd(range.from);
      if (range.to) params.end_date = localYmd(range.to);
      return params;
    }

    const now = new Date();
    const todayStr = localYmd(now);
    switch (filters.period) {
      case 'this_week':
        params.start_date = localYmd(getStartOfWeek(new Date(now)));
        params.end_date = todayStr;
        break;
      case 'this_quarter':
        params.start_date = localYmd(getStartOfQuarter(new Date(now)));
        params.end_date = todayStr;
        break;
      case 'this_year':
        params.start_date = localYmd(new Date(now.getFullYear(), 0, 1));
        params.end_date = todayStr;
        break;
      default:
        break;
    }
    return params;
  }

  if (filters.specificDate) {
    params.start_date = filters.specificDate;
    params.end_date = filters.specificDate;
    return params;
  }

  if (filters.dateFrom) params.start_date = filters.dateFrom;
  if (filters.dateTo) params.end_date = filters.dateTo;
  return params;
};

const pointsCount = (report) => report.points?.length ?? 0;

const descriptionPreview = (text, max = 80) => {
  const value = (text || '').trim();
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

const emptyForm = () => ({
  date: today,
  description: '',
  points: [{ content: '' }],
});

const getSortValue = (item, key) => {
  if (key === 'user') return item.user?.name ?? '';
  if (key === 'points_count') return pointsCount(item);
  if (key === 'date') return item.date ?? '';
  if (key === 'description') return item.description ?? '';
  return item[key] ?? '';
};

const canManageJournalReports = (user) =>
  user?.role === 'Administrateur' || user?.role === 'superAdmin';

export default function ReportsAdminIndex() {
  const user = useSelector((state) => state.auth.user);
  const userRole = user?.personnels?.[0]?.poste_id;
  const isComptable = Number(userRole) === 8;
  const Layout = isComptable ? ComptableLayout : MainLayout;
  const canManage = canManageJournalReports(user);
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_ALL);
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    search: '',
    period: '',
    userId: '',
    specificDate: '',
    ...defaultFilterDates(),
  }));
  const [draftFilters, setDraftFilters] = useState(() => ({
    userId: '',
    specificDate: '',
    ...defaultFilterDates(),
  }));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedReport, setSelectedReport] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des users :', error);
    }
  }, []);

  const fetchData = useCallback(
    async (filterOverride) => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = buildReportApiParams(filterOverride ?? appliedFilters);
        const response = await axios.get(`${APP_API_URL}/admin/journal-reports`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        setData(response.data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des rapports :', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters]
  );

  useEffect(() => {
    if (canManage) fetchUsers();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = useMemo(() => {
    const search = appliedFilters.search.trim().toLowerCase();
    if (!search) return data;
    return data.filter((item) => {
      const pointsText = (item.points || []).map((p) => p.content || '').join(' ');
      const haystack = [(item.user?.name || ''), item.date || '', item.description || '', pointsText]
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [data, appliedFilters.search]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (key === 'points_count') return (aValue - bValue) * dir;
      if (key === 'date') return (new Date(aValue) - new Date(bValue)) * dir;
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return -1 * dir;
      if (aStr > bStr) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      count: filteredItems.length,
      totalPoints: filteredItems.reduce((acc, item) => acc + pointsCount(item), 0),
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PER_PAGE));
  const indexOfLast = currentPage * PER_PAGE;
  const indexOfFirst = indexOfLast - PER_PAGE;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handlePeriodSelect = (option) => {
    setSelectedPeriod(option);
    setDraftFilters({ userId: '', specificDate: '', dateFrom: '', dateTo: '' });
    const next = {
      search: appliedFilters.search,
      period: option.value,
      userId: '',
      specificDate: '',
      dateFrom: '',
      dateTo: '',
    };
    setAppliedFilters(next);
    setCurrentPage(1);
    fetchData(next);
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setSelectedPeriod(PERIOD_ALL);
    const next = {
      search: appliedFilters.search,
      period: '',
      userId: draftFilters.userId,
      specificDate: draftFilters.specificDate,
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
    };
    setAppliedFilters(next);
    setCurrentPage(1);
    fetchData(next);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSelectedPeriod(PERIOD_ALL);
    setDraftFilters({ userId: '', specificDate: '', ...defaultFilterDates() });
    const next = {
      search: '',
      period: '',
      userId: '',
      specificDate: '',
      ...defaultFilterDates(),
    };
    setAppliedFilters(next);
    setCurrentPage(1);
    setSortConfig({ key: 'date', direction: 'desc' });
    fetchData(next);
  };

  const handleRefresh = () => fetchData();

  const handleDelete = async (report) => {
    const result = await Swal.fire({
      title: 'Supprimer ce rapport ?',
      text: `Rapport du ${formatDate(report.date)} — ${report.user?.name || ''}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff4b4b',
      cancelButtonText: 'Annuler',
      confirmButtonText: 'Supprimer',
    });
    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${APP_API_URL}/journal-reports/${report.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (selectedReport?.id === report.id) setSelectedReport(null);
      Swal.fire({ icon: 'success', title: 'Rapport supprimé', timer: 1500, showConfirmButton: false });
      fetchData();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.error || 'Suppression impossible.',
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePointChange = (index, e) => {
    const { value } = e.target;
    setFormData((prev) => {
      const points = [...prev.points];
      points[index] = { ...points[index], content: value };
      return { ...prev, points };
    });
  };

  const addPoint = () => {
    setFormData((prev) => ({
      ...prev,
      points: [...prev.points, { content: '' }],
    }));
  };

  const removePoint = (index) => {
    setFormData((prev) => ({
      ...prev,
      points: prev.points.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.date) errors.date = 'Date requise';
    const filledPoints = formData.points.filter((p) => p.content?.trim());
    if (filledPoints.length < 1) errors.points = 'Au moins un point de la journée requis';
    formData.points.forEach((point, index) => {
      if (!point.content?.trim()) errors[`point_${index}`] = 'Point requis';
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    setFormData(emptyForm());
    setFormErrors({});
    setIsAddModalOpen(true);
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        date: formData.date,
        description: formData.description?.trim() || null,
        points: formData.points
          .filter((p) => p.content?.trim())
          .map((p) => ({ content: p.content.trim() })),
      };
      await axios.post(`${APP_API_URL}/journal-reports`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsAddModalOpen(false);
      setFormData(emptyForm());
      fetchData();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du rapport :', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportRows = (items) =>
    items.map((item) => ({
      Utilisateur: item.user?.name || 'Inconnu',
      Date: formatDate(item.date),
      Description: item.description || '—',
      Points: (item.points || []).map((p) => p.content).join(' | '),
    }));

  const exportToPDF = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('Rapports journaliers', 14, 16);
    doc.autoTable({
      head: [['Utilisateur', 'Date', 'Description', 'Points de la journée']],
      body: rows.map((r) => [r.Utilisateur, r.Date, r.Description, r.Points]),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 8 },
    });
    doc.save(`Rapports ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapports');
    XLSX.writeFile(wb, `Rapports ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        (r) => `
      <tr>
        <td>${r.Utilisateur}</td><td>${r.Date}</td><td>${r.Description}</td><td>${r.Points}</td>
      </tr>`
      )
      .join('');
    printWindow.document.write(`
      <html><head><title>Rapports</title>
      <style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:6px;font-size:12px}</style>
      </head><body><h1>Rapports journaliers</h1><table>
      <thead><tr><th>Utilisateur</th><th>Date</th><th>Description</th><th>Points</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const activePeriodLabel = appliedFilters.period
    ? periodOptionsWithAll.find((p) => p.value === appliedFilters.period)?.label
    : null;

  return (
    <Layout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">{isComptable ? 'Comptabilité' : 'Analytique'}</p>
          <h1 className="duo-hero-title">Rapports journaliers</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            {canManage
              ? 'Consultation de tous les rapports. Vous pouvez supprimer un rapport si nécessaire.'
              : 'Vos rapports journaliers — seuls vos propres comptes-rendus sont visibles ici.'}
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Rapports (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.count}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Points enregistrés</p>
            <p className="duo-kpi-chip__value">{formatNumber(stats.totalPoints)}</p>
          </div>
        </div>

        <div className="mb-3">
          <DuoPeriodPicker
            options={periodOptionsWithAll}
            selectedOption={selectedPeriod}
            onSelect={handlePeriodSelect}
          />
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des rapports
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              <button type="button" className="duo-btn duo-btn--primary" onClick={openAddModal}>
                <i className="bi bi-plus-circle" /> Ajouter
              </button>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par utilisateur, description ou point..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <p className="small text-muted mb-2">
                  Filtres avancés (utilisateur, date précise ou plage personnalisée).
                </p>
                <div className="duo-filters__grid">
                  {canManage && (
                    <div>
                      <label className="duo-form-label">Utilisateur</label>
                      <select
                        className="duo-select"
                        value={draftFilters.userId}
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, userId: e.target.value }))}
                      >
                        <option value="">Tous les utilisateurs</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="duo-form-label">Date précise</label>
                    <input
                      type="date"
                      className="duo-input"
                      disabled={!!appliedFilters.period || !!draftFilters.dateFrom || !!draftFilters.dateTo}
                      value={draftFilters.specificDate}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          specificDate: e.target.value,
                          dateFrom: '',
                          dateTo: '',
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      disabled={!!appliedFilters.period || !!draftFilters.specificDate}
                      value={draftFilters.dateFrom}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value, specificDate: '' }))
                      }
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      disabled={!!appliedFilters.period || !!draftFilters.specificDate}
                      value={draftFilters.dateTo}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value, specificDate: '' }))
                      }
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2 flex-wrap">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer
                  </button>
                </div>
              </form>
            </div>
          )}

          {(activePeriodLabel ||
            (canManage && appliedFilters.userId) ||
            appliedFilters.specificDate ||
            appliedFilters.dateFrom ||
            appliedFilters.dateTo) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              {activePeriodLabel && (
                <span>
                  Période : <strong>{activePeriodLabel}</strong>
                  {' · '}
                </span>
              )}
              {canManage && appliedFilters.userId && (
                <span>
                  Utilisateur :{' '}
                  <strong>{users.find((u) => String(u.id) === String(appliedFilters.userId))?.name}</strong>
                  {' · '}
                </span>
              )}
              {appliedFilters.specificDate && (
                <span>
                  Date : <strong>{appliedFilters.specificDate}</strong>
                </span>
              )}
              {(appliedFilters.dateFrom || appliedFilters.dateTo) && !appliedFilters.specificDate && (
                <span>
                  Plage : {appliedFilters.dateFrom || '…'} → {appliedFilters.dateTo || '…'}
                </span>
              )}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  {canManage && (
                    <th onClick={() => requestSort('user')}>
                      Utilisateur <SortIcon active={isSortActive('user')} direction={sortConfig.direction} />
                    </th>
                  )}
                  <th onClick={() => requestSort('date')}>
                    Date <SortIcon active={isSortActive('date')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('description')}>
                    Description <SortIcon active={isSortActive('description')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('points_count')}>
                    Points <SortIcon active={isSortActive('points_count')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      {canManage && <td className="fw-bold">{item.user?.name || 'Inconnu'}</td>}
                      <td className="text-nowrap">{formatDate(item.date)}</td>
                      <td>{descriptionPreview(item.description)}</td>
                      <td>{pointsCount(item)}</td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                            onClick={() => setSelectedReport(item)}
                          >
                            Détails
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
                              onClick={() => handleDelete(item)}
                            >
                              Supprimer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={canManage ? 5 : 4} className="duo-empty-row">
                      Aucun rapport correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      {selectedReport && (
        <DuoModal
          size="xl"
          title="Détail du rapport journalier"
          onClose={() => setSelectedReport(null)}
          footer={
            <div className="d-flex gap-2 flex-wrap justify-content-end w-100">
              {canManage && (
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
                  onClick={() => handleDelete(selectedReport)}
                >
                  Supprimer
                </button>
              )}
              <button type="button" className="duo-btn duo-btn--ghost" onClick={() => setSelectedReport(null)}>
                Fermer
              </button>
            </div>
          }
        >
          <div className="journal-report-detail">
            <div className="journal-report-detail__meta">
              <div className="journal-report-detail__meta-item">
                <span className="journal-report-detail__meta-label">Personnel</span>
                <p className="journal-report-detail__meta-value">{selectedReport.user?.name || 'Inconnu'}</p>
              </div>
              <div className="journal-report-detail__meta-item">
                <span className="journal-report-detail__meta-label">Date</span>
                <p className="journal-report-detail__meta-value">{formatDate(selectedReport.date)}</p>
              </div>
              <div className="journal-report-detail__meta-item journal-report-detail__meta-item--accent">
                <span className="journal-report-detail__meta-label">Points</span>
                <p className="journal-report-detail__meta-value">{pointsCount(selectedReport)}</p>
              </div>
            </div>

            <section className="journal-report-detail__section">
              <h3 className="journal-report-detail__section-title">
                <i className="bi bi-card-text" aria-hidden="true" />
                Description
              </h3>
              <div className="journal-report-detail__block journal-report-detail__block--description">
                {selectedReport.description?.trim() ? (
                  <p className="journal-report-detail__text">{selectedReport.description.trim()}</p>
                ) : (
                  <p className="journal-report-detail__empty">Aucune description renseignée.</p>
                )}
              </div>
            </section>

            <section className="journal-report-detail__section">
              <h3 className="journal-report-detail__section-title">
                <i className="bi bi-list-check" aria-hidden="true" />
                Points de la journée
                {pointsCount(selectedReport) > 0 && (
                  <span className="journal-report-detail__badge">{pointsCount(selectedReport)}</span>
                )}
              </h3>
              {selectedReport.points?.length > 0 ? (
                <div className="journal-report-detail__points">
                  {selectedReport.points.map((point, idx) => (
                    <article key={idx} className="journal-report-detail__point">
                      <span className="journal-report-detail__point-num" aria-hidden="true">
                        {idx + 1}
                      </span>
                      <p className="journal-report-detail__point-text">{point.content}</p>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="journal-report-detail__block">
                  <p className="journal-report-detail__empty">Aucun point enregistré.</p>
                </div>
              )}
            </section>
          </div>
        </DuoModal>
      )}

      {isAddModalOpen && (
        <DuoModal
          size="lg"
          title="Ajouter un rapport journalier"
          onClose={() => setIsAddModalOpen(false)}
          footer={
            <>
              <button
                type="button"
                className="duo-btn duo-btn--ghost"
                onClick={() => setIsAddModalOpen(false)}
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="journal-report-form"
                className="duo-btn duo-btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <form id="journal-report-form" onSubmit={handleSubmitReport}>
            <div className="duo-form-group">
              <label className="duo-form-label">Date</label>
              <input
                type="date"
                name="date"
                className="duo-input"
                value={formData.date}
                onChange={handleInputChange}
              />
              {formErrors.date && <small className="text-danger">{formErrors.date}</small>}
            </div>

            <div className="duo-form-group">
              <label className="duo-form-label">Description</label>
              <textarea
                name="description"
                className="duo-input"
                rows={4}
                placeholder="Résumé général de la journée..."
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>

            <div className="duo-form-group">
              <label className="duo-form-label">Points de la journée</label>
              {formData.points.map((point, index) => (
                <div key={index} className="row g-2 mb-2 align-items-start">
                  <div className="col-auto pt-2 text-muted">{index + 1}.</div>
                  <div className="col">
                    <textarea
                      className="duo-input"
                      rows={2}
                      placeholder="Décrivez un point clé de la journée..."
                      value={point.content}
                      onChange={(e) => handlePointChange(index, e)}
                    />
                    {formErrors[`point_${index}`] && (
                      <small className="text-danger">{formErrors[`point_${index}`]}</small>
                    )}
                  </div>
                  <div className="col-auto">
                    <button
                      type="button"
                      className="duo-btn duo-btn--ghost"
                      onClick={() => removePoint(index)}
                      disabled={formData.points.length <= 1}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
              ))}
              {formErrors.points && <small className="text-danger d-block mb-2">{formErrors.points}</small>}
              <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={addPoint}>
                <i className="bi bi-plus-circle" /> Ajouter un point
              </button>
            </div>
          </form>
        </DuoModal>
      )}
    </Layout>
  );
}
